import Icon from "@/components/Icon/Icon";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { memo, useCallback } from "react";
import type { Attribute, FieldRenderInputProps } from "../../types";
import { getAttributeOptions } from "../../utils/option-utils";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

function getAttributeDivisionDisplayLabel(
  attribute: Attribute,
  value: string | null,
): string {
  if (!attribute) {
    return "";
  }

  if (!value) {
    return attribute.label;
  }

  const options = getAttributeOptions(attribute);
  return options.find((i) => i.value === value)?.label || attribute.label;
}

export const ChangeSingleSelectField = memo(
  ({
    fieldId,
    field,
    readOnly,
    disabled,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    const renderInput = useCallback(
      ({
        value: inputValue,
        previousValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        error: fieldError,
        placeholder,
        disabled: isDisabled,
        readOnly: isReadOnly,
        "data-testid": inputTestId,
      }: FieldRenderInputProps) => {
        const tc = useCommonTranslation();
        const selectValue =
          inputValue === null || inputValue === undefined
            ? ""
            : String(inputValue);

        const displayPreviousValue =
          previousValue === null || previousValue === undefined
            ? ""
            : String(previousValue);

        if (!field) {
          return <div></div>;
        }

        return (
          <HStack className="items-center justify-center w-full gap-4">
            {/* 現在の値（読み取り専用のテキスト表示） */}
            <div className="flex-1 justify-start min-w-0">
              <Text
                variant="labelSemiLarge"
                className="text-neutral-on-fill break-words"
              >
                {displayPreviousValue
                  ? getAttributeDivisionDisplayLabel(
                      field,
                      displayPreviousValue,
                    )
                  : tc("unregistered")}
              </Text>
            </div>

            {/* Arrow Icon */}
            <div className="items-center flex-shrink-0 self-center pr-3 pl-3">
              <Icon
                iconName="ArrowBigRightIcon"
                className="w-4 h-4 text-primary-on-neutral-fill"
              />
            </div>

            {/* 新しい値の選択 */}
            <div className="flex-1 min-w-0">
              {isReadOnly ? (
                // readonly時は選択された値をテキストとして表示
                <Text
                  variant="labelSemiLarge"
                  className="text-neutral-on-fill break-words"
                >
                  {selectValue
                    ? getAttributeDivisionDisplayLabel(field, selectValue)
                    : tc("unregistered")}
                </Text>
              ) : (
                // 通常時はSelectコンポーネントを表示
                <Select
                  value={selectValue}
                  onValueChange={(newValue) => {
                    // 空文字列はundefinedとして扱う
                    newValue === ""
                      ? handleChange(undefined)
                      : handleChange(newValue);
                  }}
                  disabled={isDisabled || isReadOnly}
                  onOpenChange={(open) => {
                    if (open && handleFocus) handleFocus();
                    if (!open && handleBlur) handleBlur();
                  }}
                >
                  <SelectTrigger
                    className={`w-full ${fieldError ? "border-red-500" : ""}`}
                    data-testid={inputTestId}
                    data-role="form-field-input"
                    aria-invalid={!!fieldError}
                    disabled={isReadOnly}
                  >
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                  <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
                    {getAttributeOptions(field).map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {getAttributeDivisionDisplayLabel(field, item.value)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </HStack>
        );
      },
      [field],
    );

    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        readOnly={readOnly}
        disabled={disabled}
        layout="vertical" // UI 的にラベルと入力フィールドが縦並びじゃないとしんどいので
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

ChangeSingleSelectField.displayName = "ChangeSingleSelectField";
